import { z } from "zod";

// Roles, request statuses, state categories, autonomy levels and link types. They live in a
// leaf module, not here: `export *` is hoisted, so a feature module below that imported them
// from this barrel would be evaluated before this file's own body and would read them from the
// temporal dead zone. Feature modules import `./enums` directly; only this barrel re-exports it.
export * from "./enums";
export * from "./errors";
// Public request identifiers live in a leaf module so feature modules (the proposal schema)
// can import them without touching this barrel.
export * from "./public-id";

// Health. Both endpoints live under /api because CloudFront routes only /api/* and /auth/*
// to the origin; readiness checks this process and its database only (§11.5).
export const HealthLiveSchema = z.object({
  status: z.literal("ok"),
  service: z.string(),
  version: z.string(),
});
export type HealthLive = z.infer<typeof HealthLiveSchema>;

export const HealthReadySchema = z.object({
  status: z.enum(["ready", "not_ready"]),
  service: z.string(),
  checks: z.object({
    database: z.enum(["ok", "failed", "skipped"]),
  }),
});
export type HealthReady = z.infer<typeof HealthReadySchema>;

// Every error response has this shape. Never a stack, never a body echo.
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export * from "./attachments";
// Runtime board configuration — read by the applier and reconciler, never by the agent.
// It imports its enums from "./enums", not from here, so this re-export carries no cycle.
export * from "./board-config";
// The initial request categories and their category-specific follow-up fields (§7.1, §7.7,
// §8.4, §8.5, §8.12). Like board-config, it imports its enums from "./enums", not from here.
export * from "./categories";
export * from "./feedback";
export * from "./freescout";
// The intake form fields (§7.1) and the state mapping (§9.4). Both import only leaf modules.
export * from "./intake";
// The /api/me shape and the sign-in denial reasons (P3). Imports leaf modules only.
export * from "./me";
// The typed proposal the agent emits and the applier consumes (§8.10). Imports leaf modules only.
export * from "./proposal";
export * from "./queue";
// The requester-facing request API shapes (§11.2, P4). Imports leaf modules only.
export * from "./requests-api";
// The staff routes: queue, stats, detail, patch, replies, and raising for a caller (§11.3, P5).
export * from "./staff-api";
export * from "./state-mapping";
export * from "./triage-review";
